import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Login as LoginIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Cloud as CloudIcon,
  Terminal as TerminalIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  Apps as AppsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { pageVariants, sectionVariants } from '../animations/variants';

// Theme color for Access Logs page
const accentColor = '#5D6D7E';

// Reusable input styles
const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: accentColor },
    '&.Mui-focused fieldset': { borderColor: accentColor },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: accentColor },
  },
};

const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    border: '1px solid #E5E5E5',
    backgroundColor: '#FFFFFF',
    maxWidth: '700px',
  },
};

const AccessLogs = () => {
  // State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ awsAccounts: [], permissionSets: [], loginTypes: [], applications: [] });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userEmail: '',
    awsAccountId: '',
    permissionSet: '',
    loginType: '',
    applicationName: '',
  });

  // Dialogs
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const apiFilters = {
        page: pagination.page + 1,
        pageSize: pagination.pageSize,
      };

      if (filters.startDate) {
        apiFilters.startDate = new Date(filters.startDate).toISOString();
      }
      if (filters.endDate) {
        apiFilters.endDate = new Date(filters.endDate).toISOString();
      }
      if (filters.userEmail) {
        apiFilters.userEmail = filters.userEmail;
      }
      if (filters.awsAccountId) {
        apiFilters.awsAccountId = filters.awsAccountId;
      }
      if (filters.permissionSet) {
        apiFilters.permissionSet = filters.permissionSet;
      }
      if (filters.loginType) {
        apiFilters.loginType = filters.loginType;
      }
      if (filters.applicationName) {
        apiFilters.applicationName = filters.applicationName;
      }

      const response = await apiService.getAccessLogs(apiFilters);
      if (response.success) {
        setLogs(response.data?.logs || []);
        setPagination(prev => ({
          ...prev,
          total: response.data?.total || 0,
          totalPages: response.data?.totalPages || 0,
        }));
      }
    } catch (err) {
      toast.error('Failed to fetch access logs');
      console.error('Failed to fetch access logs:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.getAccessStats(30);
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await apiService.getAccessLogFilterOptions();
      if (response.success) {
        setFilterOptions(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
  }, [fetchStats, fetchFilterOptions]);

  // Apply filters
  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 0 }));
    fetchLogs();
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      userEmail: '',
      awsAccountId: '',
      permissionSet: '',
      loginType: '',
      applicationName: '',
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  // View log details - fetch full log data
  const handleViewDetails = async (logId) => {
    setDetailLoading(true);
    try {
      const response = await apiService.getAccessLog(logId);
      if (response.success) {
        setSelectedLog(response.data);
      } else {
        toast.error('Failed to load log details');
      }
    } catch (err) {
      toast.error('Failed to load log details');
      console.error('Failed to fetch log details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Get display name for AWS account
  const getAccountDisplay = (log) => {
    if (log.awsAccountName) {
      return `${log.awsAccountName} (${log.awsAccountId})`;
    }
    return log.awsAccountId;
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{ minHeight: '100vh', padding: '32px', backgroundColor: '#F9FBFD' }}
    >
      {/* Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ backgroundColor: accentColor, width: 48, height: 48 }}>
              <LoginIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                Access Logs
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                {pagination.total} log entries
                {stats?.totalLogins ? ` (${stats.totalLogins} logins in last 30 days)` : ''}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Tooltip title="Toggle Filters">
              <IconButton
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  color: showFilters ? accentColor : '#666666',
                  backgroundColor: showFilters ? `${accentColor}15` : '#F9FBFD',
                  border: `1px solid ${showFilters ? accentColor : '#E5E5E5'}`,
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: '#F0F4F8' },
                }}
              >
                <FilterIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchLogs}
                disabled={loading}
                sx={{
                  color: '#666666',
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: '#F0F4F8' },
                }}
              >
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Stats Cards */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Grid container spacing={2}>
        {[
          { icon: <LoginIcon sx={{ color: '#4698D3', fontSize: 20 }} />, color: '#4698D3', label: 'Total Logins (30 days)', value: stats?.totalLogins },
          { icon: <PersonIcon sx={{ color: '#27AE60', fontSize: 20 }} />, color: '#27AE60', label: 'Unique Users (30 days)', value: stats?.uniqueUsers },
          { icon: <CloudIcon sx={{ color: '#F39C12', fontSize: 20 }} />, color: '#F39C12', label: 'AWS Accounts Accessed', value: stats?.uniqueAccounts },
          { icon: <VpnKeyIcon sx={{ color: '#9B59B6', fontSize: 20 }} />, color: '#9B59B6', label: 'Permission Sets Used', value: stats?.uniquePermissionSets },
        ].map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Card elevation={0} sx={{ border: '1px solid #E5E5E5', borderRadius: '8px' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {card.icon}
                  {stats ? (
                    <Typography variant="h5" sx={{ color: card.color, fontWeight: 600, fontFamily: 'Metropolis, sans-serif' }}>
                      {card.value || 0}
                    </Typography>
                  ) : (
                    <CircularProgress size={24} sx={{ color: card.color }} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                  {card.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {stats && stats.uniqueApplications > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #E5E5E5', borderRadius: '8px' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AppsIcon sx={{ color: '#8E44AD', fontSize: 20 }} />
                  <Typography variant="h5" sx={{ color: '#8E44AD', fontWeight: 600, fontFamily: 'Metropolis, sans-serif' }}>
                    {stats.uniqueApplications}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                  Custom Apps Accessed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      </motion.div>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#253E66',
              mb: 2,
            }}
          >
            Filter Logs
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1: Date pickers and dropdowns */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ ...inputStyle, minWidth: 150, flex: '1 1 150px', maxWidth: 180 }}
              />
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ ...inputStyle, minWidth: 150, flex: '1 1 150px', maxWidth: 180 }}
              />
              <FormControl size="small" sx={{ ...inputStyle, minWidth: 200, flex: '1 1 200px', maxWidth: 280 }}>
                <InputLabel>AWS Account</InputLabel>
                <Select
                  value={filters.awsAccountId}
                  onChange={(e) => setFilters(prev => ({ ...prev, awsAccountId: e.target.value }))}
                  label="AWS Account"
                >
                  <MenuItem value="">All Accounts</MenuItem>
                  {filterOptions.awsAccounts.map((account) => (
                    <MenuItem key={account} value={account}>
                      {account}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ ...inputStyle, minWidth: 180, flex: '1 1 180px', maxWidth: 260 }}>
                <InputLabel>Permission Set</InputLabel>
                <Select
                  value={filters.permissionSet}
                  onChange={(e) => setFilters(prev => ({ ...prev, permissionSet: e.target.value }))}
                  label="Permission Set"
                >
                  <MenuItem value="">All Permission Sets</MenuItem>
                  {filterOptions.permissionSets.map((permSet) => (
                    <MenuItem key={permSet} value={permSet}>
                      {permSet}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ ...inputStyle, minWidth: 150, flex: '1 1 150px', maxWidth: 200 }}>
                <InputLabel>Login Type</InputLabel>
                <Select
                  value={filters.loginType}
                  onChange={(e) => setFilters(prev => ({ ...prev, loginType: e.target.value }))}
                  label="Login Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {filterOptions.loginTypes.map((lt) => (
                    <MenuItem key={lt} value={lt}>
                      {lt === 'aws_console' ? 'AWS Console' : lt === 'custom_app' ? 'Custom App' : lt === 'cli' ? 'CLI' : lt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {filterOptions.applications.length > 0 && (
                <FormControl size="small" sx={{ ...inputStyle, minWidth: 180, flex: '1 1 180px', maxWidth: 260 }}>
                  <InputLabel>Application</InputLabel>
                  <Select
                    value={filters.applicationName}
                    onChange={(e) => setFilters(prev => ({ ...prev, applicationName: e.target.value }))}
                    label="Application"
                  >
                    <MenuItem value="">All Applications</MenuItem>
                    {filterOptions.applications.map((app) => (
                      <MenuItem key={app} value={app}>
                        {app}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Row 2: Search fields and buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                label="User Email"
                value={filters.userEmail}
                onChange={(e) => setFilters(prev => ({ ...prev, userEmail: e.target.value }))}
                placeholder="Search by email..."
                sx={{ ...inputStyle, minWidth: 250, flex: '1 1 250px', maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999999', fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  startIcon={<SearchIcon />}
                  sx={{
                    backgroundColor: accentColor,
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 3,
                    '&:hover': { backgroundColor: '#4A5A6A' },
                  }}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  sx={{
                    borderColor: '#E5E5E5',
                    color: '#666666',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 2,
                    '&:hover': { borderColor: '#999999', backgroundColor: '#F9FBFD' },
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Collapse>

      {/* Logs Table */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Timestamp
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  User Email
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Target
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Permission Set
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Client IP
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} sx={{ color: accentColor }} />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                      No access logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, index) => (
                  <TableRow
                    key={log.id}
                    hover
                    sx={{
                      '@keyframes fadeInRow': {
                        from: { opacity: 0, transform: 'translateY(8px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                    }}
                  >
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.userEmail}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                      {log.loginType === 'custom_app' ? (
                        <Chip
                          size="small"
                          icon={<AppsIcon sx={{ fontSize: 14 }} />}
                          label={log.applicationName || 'Custom App'}
                          sx={{
                            backgroundColor: 'rgba(142, 68, 173, 0.1)',
                            color: '#8E44AD',
                            fontFamily: 'Metropolis, sans-serif',
                            '& .MuiChip-icon': { color: '#8E44AD' },
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={log.loginType === 'cli' ? <TerminalIcon sx={{ fontSize: 14 }} /> : <CloudIcon sx={{ fontSize: 14 }} />}
                          label={getAccountDisplay(log)}
                          sx={{
                            backgroundColor: log.loginType === 'cli' ? 'rgba(39, 174, 96, 0.1)' : 'rgba(70, 152, 211, 0.1)',
                            color: log.loginType === 'cli' ? '#27AE60' : '#4698D3',
                            fontFamily: 'Metropolis, sans-serif',
                            '& .MuiChip-icon': { color: log.loginType === 'cli' ? '#27AE60' : '#4698D3' },
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                      {log.loginType === 'custom_app' ? (
                        <Typography sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>&mdash;</Typography>
                      ) : (
                        <Chip
                          size="small"
                          icon={<VpnKeyIcon sx={{ fontSize: 14 }} />}
                          label={log.permissionSet}
                          variant="outlined"
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            '& .MuiChip-icon': { color: '#666666' },
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>
                      {log.clientIp}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(log.id)}
                          sx={{ color: accentColor }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          rowsPerPage={pagination.pageSize}
          onRowsPerPageChange={(e) =>
            setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #E5E5E5',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: 'Metropolis, sans-serif',
            },
          }}
        />
      </Paper>
      </motion.div>

      {/* Log Detail Dialog */}
      <Dialog
        open={!!selectedLog || detailLoading}
        onClose={() => setSelectedLog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        {detailLoading ? (
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress sx={{ color: accentColor }} />
          </DialogContent>
        ) : selectedLog && (
          <>
            <DialogTitle
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                color: '#253E66',
                borderBottom: '1px solid #E5E5E5',
              }}
            >
              Access Log Details
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Timestamp
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {formatTimestamp(selectedLog.timestamp)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    User Email
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {selectedLog.userEmail}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                {selectedLog.loginType === 'custom_app' ? (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                        Login Type
                      </Typography>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        Custom Application
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                        Application Name
                      </Typography>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        {selectedLog.applicationName || '-'}
                      </Typography>
                    </Grid>
                    {selectedLog.applicationId && (
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                          Application ID
                        </Typography>
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                          {selectedLog.applicationId}
                        </Typography>
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                        AWS Account ID
                      </Typography>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        {selectedLog.awsAccountId}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                        AWS Account Name
                      </Typography>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        {selectedLog.awsAccountName || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                        Permission Set
                      </Typography>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        {selectedLog.permissionSet}
                      </Typography>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Client IP
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {selectedLog.clientIp}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    SAML Issuer
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                    {selectedLog.samlIssuer || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    User Agent
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.75rem',
                      wordBreak: 'break-all',
                      color: '#666666',
                    }}
                  >
                    {selectedLog.userAgent || '-'}
                  </Typography>
                </Grid>
              </Grid>
              </motion.div>
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid #E5E5E5', px: 3, py: 2 }}>
              <Button
                onClick={() => setSelectedLog(null)}
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </motion.div>
  );
};

export default AccessLogs;
